import React, { useEffect, useState } from "react";
import { TspFormField } from "../primitives";
import TspApiService from "../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../hooks/useTspApiResource";
import { OTHER_CITY, PLACE_KIND, tidyName } from "../../utils/tspPlace";

const AREA_MIN = 2;
const AREA_MAX = 100;
const NO_OPTIONS = Promise.resolve({ data: [] });

// Pick a place for a route: State -> District -> City. Districts and cities come
// from the API (every state and district of India, and each district's towns),
// each list loading only once the level above is chosen. When the place is not
// among the district's towns the person chooses "Other" and an Area box appears
// for typing it by hand.
//
// `onAdd(place)` receives { stateId, state, districtId, district, cityId, city,
// kind } (see utils/tspPlace.js) and returns { ok } or { ok: false, message }: a
// message is shown under the fields. After a successful add the city / area are
// cleared but the state and district stay, so several places in one district
// can be added quickly. `error` is a message from the surrounding form (for
// example "Add at least one city").
//
// `preset` ({ key, stateId, districtId, cityId, city, kind }) fills the fields
// with a place known in advance (e.g. from a point picked on the map), each
// level as far as it is known; a NEW `key` applies it again. The person can
// change every field before adding.
// `extraFields` (optional) is rendered in the row with the Add button, before it
// (Add Journey puts the place's Visit Date there).
export default function TspPlacePicker({
  idPrefix,
  states,
  onAdd,
  addLabel = "Add Taluka / Town / Municipality",
  disabled = false,
  error,
  preset = null,
  extraFields = null,
  onSelectionChange = null,
}) {
  const [stateId, setStateId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [cityId, setCityId] = useState("");
  const [area, setArea] = useState("");
  const [addError, setAddError] = useState("");

  const presetKey = preset ? preset.key : null;
  useEffect(() => {
    if (!preset) return;
    const hasDistrict = Boolean(preset.stateId && preset.districtId);
    const isArea = hasDistrict && !preset.cityId && Boolean(preset.city);
    const nextStateId = preset.stateId || "";
    const nextDistrictId = hasDistrict ? preset.districtId : "";
    const nextCityId = !hasDistrict ? "" : isArea ? OTHER_CITY : preset.cityId || "";
    const nextArea = isArea ? preset.city : "";
    setStateId(nextStateId);
    setDistrictId(nextDistrictId);
    setCityId(nextCityId);
    setArea(nextArea);
    setAddError("");
    onSelectionChange?.({
      stateId: nextStateId,
      districtId: nextDistrictId,
      cityId: nextCityId,
      area: nextArea,
      isOther: isArea,
      latitude: preset.latitude,
      longitude: preset.longitude,
    });
    // Applied once per preset key: later edits by the person are theirs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const districts = useTspApiResource(() => (stateId ? TspApiService.listDistricts(stateId) : NO_OPTIONS), [stateId]);
  const cities = useTspApiResource(() => (districtId ? TspApiService.listCities(districtId) : NO_OPTIONS), [districtId]);

  const districtList = districts.data || [];
  const cityList = cities.data || [];
  const isOther = cityId === OTHER_CITY;
  const areaName = tidyName(area);

  const state = states.find((candidate) => String(candidate.id) === String(stateId));
  const district = districtList.find((candidate) => String(candidate.id) === districtId);
  const city = cityList.find((candidate) => String(candidate.id) === cityId);
  const canAdd = Boolean(state && district && (isOther ? areaName.length >= AREA_MIN : city));

  const chooseState = (event) => {
    const val = event.target.value;
    setStateId(val);
    setDistrictId("");
    setCityId("");
    setArea("");
    setAddError("");
    onSelectionChange?.({ stateId: val, districtId: "", cityId: "", area: "", isOther: false });
  };

  const chooseDistrict = (event) => {
    const val = event.target.value;
    setDistrictId(val);
    setCityId("");
    setArea("");
    setAddError("");
    onSelectionChange?.({ stateId, districtId: val, cityId: "", area: "", isOther: false });
  };

  const chooseCity = (event) => {
    const val = event.target.value;
    setCityId(val);
    setAddError("");
    onSelectionChange?.({ stateId, districtId, cityId: val, area: val === OTHER_CITY ? area : "", isOther: val === OTHER_CITY });
  };

  const chooseArea = (event) => {
    const val = event.target.value;
    setArea(val);
    setAddError("");
    onSelectionChange?.({ stateId, districtId, cityId, area: val, isOther: true });
  };

  const handleAdd = () => {
    if (!canAdd || disabled) return;
    const place = { stateId, state: state.name, districtId, district: district.name };
    const result = onAdd(isOther ? { ...place, cityId: "", city: areaName, kind: PLACE_KIND.area } : { ...place, cityId, city: city.name, kind: PLACE_KIND.city });
    const settle = (outcome) => {
      if (outcome.ok) {
        // A listed taluka/town is cleared; "Other" stays chosen (only its area is cleared)
        // so several unlisted areas in one district can be added in a row.
        if (!isOther) setCityId("");
        setArea("");
        setAddError("");
      } else {
        setAddError(outcome.message);
      }
    };
    // onAdd may also save first (Edit Journey) and return a promise of the same result.
    if (result && typeof result.then === "function") result.then(settle);
    else settle(result);
  };

  const message = addError || error;
  const errorId = `${idPrefix}-error`;
  const describedBy = message ? errorId : undefined;
  const loadFailure = districts.error ? { what: "districts", reload: districts.reload, error: districts.error } : cities.error ? { what: "talukas / towns", reload: cities.reload, error: cities.error } : null;

  const cityPlaceholder = !districtId ? "Select a district first" : cities.loading ? "Loading…" : "Select Taluka / Town / Municipality";

  return (
    <div className="tsp-place-picker">
      <TspFormField id={`${idPrefix}-state`} label="State">
        <select id={`${idPrefix}-state`} className="tsp-field-control" value={stateId} aria-invalid={error ? "true" : undefined} aria-describedby={describedBy} disabled={disabled} onChange={chooseState}>
          <option value="">Select state</option>
          {states.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </TspFormField>

      <TspFormField id={`${idPrefix}-district`} label="District">
        <select
          id={`${idPrefix}-district`}
          className="tsp-field-control"
          value={districtId}
          disabled={disabled || !stateId || districts.loading}
          onChange={chooseDistrict}
        >
          <option value="">{!stateId ? "Select a state first" : districts.loading ? "Loading districts…" : "Select district"}</option>
          {districtList.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </TspFormField>

      <TspFormField
        id={`${idPrefix}-city`}
        label="Taluka / Town / Municipality"
        hint={districtId && !cities.loading && !cities.error && cityList.length === 0 ? "No locations are listed for this district. Choose Other and enter the area." : undefined}
      >
        <select
          id={`${idPrefix}-city`}
          className="tsp-field-control"
          value={cityId}
          disabled={disabled || !districtId || cities.loading}
          onChange={chooseCity}
        >
          <option value="">{cityPlaceholder}</option>
          {cityList.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
          {districtId && !cities.loading && <option value={OTHER_CITY}>Other (location not listed)</option>}
        </select>
      </TspFormField>

      <div className="tsp-place-row">
        {isOther && (
          <TspFormField id={`${idPrefix}-area`} label="Area (Optional)" hint="The location is not listed, so type its name — or the village, locality or area you will visit." className="tsp-place-area">
            <input
              id={`${idPrefix}-area`}
              type="text"
              className="tsp-field-control"
              value={area}
              maxLength={AREA_MAX}
              autoComplete="off"
              placeholder="e.g. Kheda Gaon"
              aria-required="true"
              disabled={disabled}
              onChange={chooseArea}
              onKeyDown={(event) => {
                // Enter adds the place; it must not submit the surrounding form.
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAdd();
                }
              }}
            />
          </TspFormField>
        )}
        {extraFields}
        <button type="button" className="tsp-btn tsp-btn-red tsp-route-add-btn tsp-place-add" onClick={handleAdd} disabled={disabled || !canAdd}>
          {addLabel}
        </button>
      </div>

      {loadFailure && (
        <p className="tsp-form-error tsp-place-message" role="alert">
          {tspErrorMessage(loadFailure.error, `Could not load the ${loadFailure.what}.`)}{" "}
          <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" onClick={loadFailure.reload}>
            Try again
          </button>
        </p>
      )}
      {message && (
        <p id={errorId} className="tsp-form-error tsp-place-message" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
